import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'
import { DAIMI_CG_SLUG } from '@/content/daimiConsumerGoods'

/**
 * The Future 2030 Consumer Goods outlook page — its copy and its figures, apart
 * from the component that draws them.
 *
 * **The third Future 2030 outlook with a designed page of its own**, and the
 * twelfth report overall. Asked for on 2026-08-28, in the same words as the
 * Automotive and Manufacturing ones — *"referans alarak ilgili sayfayı birebir
 * tıpa tıp aynısını tasarla"* — as a five-part full-page capture of digitopia.co's
 * Consumer Goods outlook page, plus the instruction that the maturity band point
 * at the 2026 DAIMI report rather than the 2024 one the live page offers.
 *
 * So the bands are the reference's, in the reference's order: hero → the outlook
 * beside Figure 1 → a dark band of three requirements → the five predictions with
 * the mockup → the pointer at the maturity report → the close. The two shared
 * closing bands (`content/reportClosing.ts`) still sit where they sit on every
 * other report page.
 *
 * **The shape is the Manufacturing page's, not the Automotive one's**: the outlook
 * band wraps its three trajectories in a lead-in and a closing line, the
 * predictions are five plain statements under a lead-in rather than
 * label-and-figure pairs, and the dark band's second line is a title —
 * "3 Requirements to Win in 2030" — rather than a second claim. Two bands differ
 * from Manufacturing's: the maturity band here is a heading, a paragraph and one
 * button with no bullets under it, and the close is a centred invitation rather
 * than a paragraph about the sector.
 *
 * **The images are the gap in this request, and it is a real one.** Six files came
 * with the captures and all six belong to a *different* report: they are the
 * Energy & Utilities outlook's hero art, its DMI chart, its three requirement
 * photographs and its mockup — wind turbines, pylons and a solar field, and a
 * chart whose 2029 leader value is 4.22 where the Consumer Goods chart in the
 * capture reads 4.12. Putting them on this page would print the wrong industry and
 * the wrong numbers, so none of them is used. The hero art, Figure 1 and the three
 * requirement photographs are therefore absent and each carries an `asset` note
 * asking for the Consumer Goods set; the predictions band shows the Future 2030
 * Consumer Goods cover this site already holds, in place of the two-copy mockup.
 *
 * **Where the words come from.** Everything here is transcribed from the five
 * captures, which are full-page renders read down to fit — the *shape* of the page
 * is certain and the *wording* is a careful reading of body copy set a handful of
 * pixels tall. Every band that carries prose therefore carries a `check`, the same
 * way `content/outlookManufacturing.ts` does, and `outlookConsumerGoodsChecks()`
 * feeds them into the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form. These documents are prerendered with
 * no client bundle at all (`src/prerender.tsx`), so a form script would never run —
 * and the other thirty reports on this site hand the reader the PDF from our own
 * bucket instead. Every Download button here is that PDF, and
 * `OUTLOOK_CG_CLOSE.check` is what keeps the gating question open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * Unlike the Automotive and Manufacturing outlooks this slug is not a `…-outlook-2030`
 * one: it is the long slug digitopia.co uses and the one the request named, and it
 * was already in `REPORTS` before this page existed. Changing it would break the
 * inbound link, so the page is built under it as it stands.
 */
export const OUTLOOK_CG_SLUG
  = 'future-2030-digital-transformation-outlook-for-consumer-goods-industry'

/**
 * The hero.
 *
 * `lede` is set bold above the paragraph because that is how the live hero sets it
 * — one claim in bold, the invitation underneath — and the button says "Download
 * the Report" rather than the "Download Report" every other band uses, which is
 * also the live page's wording.
 *
 * No `photo`: the live hero carries the shopping trolley on a circuit board, which
 * is the artwork behind the report cover with none of its lettering on it, and that
 * file was not among the ones supplied. The cover *is* held here, but it is a 400px
 * square and it is already doing the mockup's job further down the page, so the
 * hero runs as one column until the artwork arrives.
 */
export const OUTLOOK_CG_INTRO: {
  lede: string
  body: string
  downloadLabel: string
  photo?: Photo
  check: string
} = {
  lede: 'The next five years will redefine how consumer goods companies operate, compete, and grow.',
  body: 'Discover where the consumer goods industry is heading. Benchmark your digital maturity, uncover what sets leaders apart, and get ready to act.',
  downloadLabel: 'Download the Report',
  check:
    'Two things. The hero’s two lines are transcribed from the reference capture rather than supplied as text — read them back against digitopia.co before production. And the hero image is missing: the live page sets the shopping trolley on a circuit board to the right of the words, and the six images attached with this request are the Energy & Utilities outlook’s rather than this one’s. Send the Consumer Goods hero artwork and it goes straight in.',
}

/**
 * The outlook band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one block on the live page in which each line opens with a bolded
 * label — "Leaders", "Average companies", "Laggards". Split into `label` and `body`
 * here so the label can be set rather than emphasised inside a sentence, which is
 * the call every designed report page on this site has made.
 *
 * `intro` and `outro` are the two single lines the live band wraps them in, the
 * same way the Manufacturing band does.
 *
 * No `photo` or `caption`: the chart supplied with this request is the Energy &
 * Utilities one. The caption is written out all the same, because it is legible in
 * the capture and it is what the figure will be captioned with when it arrives.
 */
export const OUTLOOK_CG_FIGURE: {
  heading: string
  intro: string
  points: { label: string; body: string }[]
  outro: string
  photo?: Photo
  caption: string[]
  check: string
} = {
  heading: 'Consumer Goods Industry Outlook',
  intro: 'Digitopia’s forecast shows three possible futures:',
  points: [
    {
      label: 'Leaders',
      body: 'will pull further ahead with AI-driven strategies, seamless customer experiences, and operational excellence.',
    },
    {
      label: 'Average companies',
      body: 'risk falling behind unless they accelerate investment and break out of incrementalism.',
    },
    {
      label: 'Laggards',
      body: 'face existential risk, but with bold moves today, they could leap ahead and redefine their trajectory.',
    },
  ],
  outro: 'Your actions today will determine whether you thrive or just survive.',
  caption: [
    'Figure 1: Consumer Goods Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'Three things here, and the first is a missing file. Figure 1 is absent: the chart attached with this request is the Energy & Utilities one — its 2029 leader value reads 4.22 where the Consumer Goods chart in your own capture reads 4.12 — so it is not shown and the caption stands alone until the right file arrives. Second, the Leaders line is the least certain sentence on this page: the sticky header covers its opening clause in the capture, so only “…AI-driven strategies, seamless customer experiences, and operational excellence” is legible and the rest is read from the shape of the line. Read it back. Third, the live page calls the index DMI here while every other page on this site calls it DAIMI, the Digital & AI Maturity Index; the chart itself is lettered DMI, so both were left as they are. Say which name this page should use.',
}

/**
 * The dark requirements band: two headline lines, then three cards.
 *
 * The live band's second line is not a sentence but a title — "3 Requirements to
 * Win in 2030" — so it is set lighter and larger under the headline rather than as
 * a second claim, which is what the Manufacturing band already does with its own.
 *
 * None of the three cards has a `photo`. The live band sets a square photograph
 * over each — a shopper with a basket, a phone conjuring gift boxes, a shop
 * assistant with a tablet — and the three squares attached with this request are
 * the Energy & Utilities band's instead.
 */
export const OUTLOOK_CG_REQUIREMENTS: {
  heading: string
  subheading: string
  eyebrow: string
  items: {
    title: string
    body: string
    linkLabel: string
    linkHref: string
    photo?: Photo
  }[]
  check: string
} = {
  heading: 'The gap will widen unless you close it now.',
  subheading: '3 Requirements to Win in 2030',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'Prioritize the Experience',
      body: 'Every digital initiative must enhance the consumer journey, from personalized product design to seamless post-purchase support.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
    },
    {
      title: 'Go All-In on AI',
      body: 'Adopt an AI-first mindset across every function, from forecasting to fulfillment, to drive responsiveness and innovation.',
      linkLabel: 'Artificial Intelligence',
      linkHref: LINKS.enterpriseAi,
    },
    {
      title: 'Empower Your People',
      body: 'Invest in skills, mindset, and collaboration to ensure your teams can fully embrace change and deliver innovation at scale.',
      linkLabel: 'Digital Upskilling',
      linkHref: LINKS.transformation,
    },
  ],
  check:
    'Three things. The two headline lines and the three card texts are transcribed from the reference capture. The three square photographs are missing for the reason the rest of this page’s images are — the squares supplied belong to the Energy & Utilities outlook — so the cards run as text until the consumer goods set arrives. And the third card’s link is the one destination that had to change: the live page sends “Digital Upskilling” to a solution page digitopia.co has and this site does not, so it points at Transformation Management until that page is built. This is the same substitution the Automotive and Manufacturing outlook pages make, and one answer settles all three.',
}

/**
 * The predictions band, with the report cover beside it.
 *
 * Five plain statements rather than the Automotive page's label-and-figure pairs,
 * because that is what the live band prints: a lead-in paragraph and five bullets.
 *
 * `photo` is the Future 2030 Consumer Goods **cover**, which this site already
 * holds, standing in for the live band's two-copy mockup — the mockup is two
 * printings of exactly this artwork, so it is the one substitution on this page
 * that shows the reader the right thing.
 */
export const OUTLOOK_CG_PREDICTIONS = {
  heading: 'Five Predictions That Will Reshape Consumer Goods',
  intro:
    'Explore the five most critical shifts shaping the future of consumer goods. These predictions are based on extensive market research and DMI trend data.',
  items: [
    'Hyper-Automated Supply Chains',
    'Evolution of Direct-to-Consumer Models',
    'Rise of Digital Ecosystems',
    'Scalable Mass-Customization',
    'Sustainability as a Brand Driver',
  ],
  photo: {
    src: '/photo/future-2030-outlook-consumer-goods-cover.webp',
    alt: 'Future 2030: Digital Transformation Outlook for Consumer Goods',
  } satisfies Photo,
  check:
    'All five predictions are transcribed from the reference capture. None of them carries a figure, so what is wanted here is a reading of the wording rather than a check of a number. The image beside them is the report cover rather than the live band’s two-copy mockup, and it is held at 400px square — send the mockup and it replaces this at the size the reference shows.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's, and the only
 * change in this request that was asked for in words rather than shown in a
 * picture.** digitopia.co sends the reader to a *2024* Consumer Goods Digital
 * Maturity Report; the instruction with the captures was *"en aşağıda önceden
 * konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. So the CTA,
 * the cover and the sentence all name the Consumer Goods Industry Focus cut of the
 * Digital & AI Maturity Report 2026 — which is the report this site actually
 * publishes, and which has a designed page of its own
 * (`content/daimiConsumerGoods.ts`).
 *
 * That is settled, so it is not what `check` asks about: the same swap was left
 * open on the Automotive page and has now been answered three times.
 *
 * Unlike the Automotive and Manufacturing bands this one has no bullets under the
 * paragraph — the live band is a heading, one paragraph and one button, and that
 * is what it is here.
 */
export const OUTLOOK_CG_MATURITY = {
  heading: 'How Does Your Digital Maturity Compare?',
  body: 'The Consumer Goods Industry Focus cut of the Digital & AI Maturity Report 2026 reveals where companies stand today, and where they need to go. Compare your performance with leaders, average performers, and laggards. Digitopia’s DMI data shows a growing gap between those moving fast and those falling behind.',
  ctaLabel: 'Explore the Consumer Goods Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_CG_SLUG}`,
  /** That report's cover, which this site already holds at 2560px. */
  photo: {
    src: '/photo/daimi-report-2026-consumer-goods-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Consumer Goods Focus',
  } satisfies Photo,
  check:
    'The destination here is decided, not transcribed: this band points at the 2026 report rather than the “2024 Consumer Goods Digital Maturity Report” the live page offers, which is what you asked for — so the cover, the CTA label and the sentence naming the report are all the 2026 cut’s. What still wants a reading is the rest of the paragraph, which is the live one word for word.',
}

/**
 * The closing band, which is where the live page's gated form sits.
 *
 * Centred, and shorter than the other outlooks' closes: the live band is a
 * headline, one line of invitation and then the form.
 */
export const OUTLOOK_CG_CLOSE = {
  heading: 'Let’s Turn Insight into Action',
  body: 'Download the full report to discover your competitive position and start building your 2030 roadmap today.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `outlookManufacturingChecks()` uses, and appended
 * to the same list in `reportChecks()`.
 *
 * Unlike the other two outlooks these are `'asset'` rather than `'content'`, because
 * on this page the two kinds are tangled: four of the six notes are partly about
 * a file that never arrived, and marking the set as content would let a missing
 * hero image read as a wording question. `severity` only sorts the notes on
 * `/__checks`; both kinds gate a production build the same way.
 */
export function outlookConsumerGoodsChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_CG_INTRO.check },
    { section: 'outlook', check: OUTLOOK_CG_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_CG_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_CG_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_CG_MATURITY.check },
    { section: 'download', check: OUTLOOK_CG_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_CG_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'asset',
    }))
}
